import { createSlice } from "@reduxjs/toolkit";
import {
  BaseResponse,
  ICustomerBillsResponse,
  ICustomerResponse,
  IcustomerInsightResponse,
  IcustomerLatestBillDetailResponse,
  IcustomerServiceUsageResponse,
  IcustomerAvgSpendingResponse,
  ICustomerBill,
  ICustomerNfcTagResponse,
  ICustomerRefundStatisticsResponse,
  ICustomerRefundHistoryResponse,
  ICustomerReportHistoryResponse,
} from "../../typings";
import {
  addStoreCreditAction,
  addUserNoteAction,
  assignCustomerNfcTagAction,
  addCustomerNfcTagAction,
  removeCustomerNfcTagAction,
  deleteUserNoteAction,
  getCustomerBillHistoryAction,
  getCustomerByIdAction,
  getCustomerCartInfoByIdAction,
  getStoreCreditHistoryAction,
  getUserNotesByIdAction,
  updateUserNoteAction,
  deleteStoreCreditAction,
  getInvoiceHistoryAction,
} from "../actions/customerAction";
import { CustomerCartInfoResponse } from "../../typings/interfaces/responses/customer-cart-info-response";
import { CustomerNoteResponse } from "../../typings/interfaces/responses/customer-note-response";
import { StoreCreditHistoryResponse } from "../../typings/interfaces/responses/store-credit-history-response";
import {
  getAllCustomerInsightsAction,
  getCustomerActivitiesByDayAction,
  getCustomerOutstandingAction,
} from "../actions/customerInsightsAction";
import { getAllCustomerLatestBillDetailsAction } from "../actions/customerLatestBillDetailsAction";
import {
  getAllCustomerCurrentYearServiceUsageAction,
  getAllCustomerAllTimeServiceUsageAction,
} from "../actions/customerServiceUsageAction";
import { getAllCustomerAvgSpendingsAction } from "../actions/customerAvgSpendingsAction";
import {
  getCustomerRefundStatsAction,
  getCustomerRefundStatsByYearAction,
  getCustomerRefundHistoryAction,
} from "../actions/customerRefundAction";
import { getCustomerReportHistoryAction } from "../actions/customerReportHistoryAction";
import { ICustomerActivitiesByDayResponse } from "../../typings/interfaces/responses/customer-activities-by-day-response";
import { ICustomerOutstandingResponse } from "../../typings/interfaces/responses/customer-outstanding-response";
import { InvoiceHistoryResponse } from "../../typings/interfaces/responses/invoice-history-response";
import { ICustomerGeneratedReport } from "../../typings/interfaces/responses/customer-generated-report-response";
import { IMonthlyInvoice } from "../../typings/interfaces/responses/monthly-invoice-response";
import { ICustomerReportData } from "../../typings/interfaces/responses/customer-report-data-response";
import {
  generateCustomerReportAction,
  getCustomerGeneratedReportsAction,
  getCustomerMonthlyInvoicesAction,
  getIsMonthlyCustomerAction,
} from "../actions/customerGeneratedReportsAction";
import { getCustomerReportDataAction } from "../actions/getCustomerReportDataAction";

export interface CustomerState {
  customer: ICustomerResponse | null;
  customerLoading: boolean;
  customerError: string | null;

  customerCartInfo: CustomerCartInfoResponse | null;
  customerCartInfoLoading: boolean;
  customerCartInfoError: string | null;

  createCustomerResponse: BaseResponse<ICustomerResponse> | null;
  createCustomerLoading: boolean;
  createCustomerError: string | null;

  updateCustomerResponse: BaseResponse<ICustomerResponse> | null;
  updateCustomerLoading: boolean;
  updateCustomerError: string | null;

  bills: ICustomerBillsResponse | null;
  billsLoading: boolean;
  billsError: string | null;

  addStoreCreditResponse: BaseResponse<any> | null;
  addStoreCreditLoading: boolean;
  addStoreCreditError: string | null;

  deleteStoreCreditResponse: BaseResponse<any> | null;
  deleteStoreCreditLoading: boolean;
  deleteStoreCreditError: string | null;

  storeCreditHistoryResponse: BaseResponse<StoreCreditHistoryResponse> | null;
  getStoreCreditHistoryLoading: boolean;
  getStoreCreditHistoryError: string | null;

  addUserNoteResponse: BaseResponse<any> | null;
  addUserNoteLoading: boolean;
  addUserNoteError: string | null;

  updateUserNoteResponse: BaseResponse<any> | null;
  updateUserNoteLoading: boolean;
  updateUserNoteError: string | null;

  customerNotes: Array<CustomerNoteResponse> | null;
  customerNotesLoading: boolean;
  customerNotesError: string | null;

  deleteCustomerNoteResponse: BaseResponse<any> | null;
  deleteCustomerNoteLoading: boolean;
  deleteCustomerNoteError: string | null;

  selectedBill: ICustomerBillsResponse | null;

  customerInsights: IcustomerInsightResponse | null;
  customerInsightsLoading: boolean;
  customerInsightsError: string | null;

  customerLatestBillDetails: IcustomerLatestBillDetailResponse | null;
  customerLatestBillDetailsLoading: boolean;
  customerLatestBillDetailsError: string | null;

  customerCurrentYearServiceUsage: IcustomerServiceUsageResponse[] | null;
  customerCurrentYearServiceUsageLoading: boolean;
  customerCurrentYearServiceUsageError: string | null;

  customerAllTimeServiceUsage: IcustomerServiceUsageResponse[] | null;
  customerAllTimeServiceUsageLoading: boolean;
  customerAllTimeServiceUsageError: string | null;

  customerAvgSpendings: IcustomerAvgSpendingResponse | null;
  customerAvgSpendingsLoading: boolean;
  customerAvgSpendingsError: string | null;

  customerActivitiesByDay: ICustomerActivitiesByDayResponse | null;
  customerActivitiesByDayLoading: boolean;
  customerActivitiesByDayError: string | null;

  customerOutstanding: ICustomerOutstandingResponse | null;
  customerOutstandingLoading: boolean;
  customerOutstandingError: string | null;

  invoiceHistory: InvoiceHistoryResponse[] | null;
  invoiceHistoryLoading: boolean;
  invoiceHistoryError: string | null;

  assignNfcTagResponse: ICustomerResponse | null;
  assignNfcTagLoading: boolean;
  assignNfcTagError: string | null;

  addNfcTagResponse: ICustomerNfcTagResponse | null;
  addNfcTagLoading: boolean;
  addNfcTagError: string | null;

  removeNfcTagLoading: boolean;
  removeNfcTagError: string | null;

  customerRefundStats: ICustomerRefundStatisticsResponse | null;
  customerRefundStatsLoading: boolean;
  customerRefundStatsError: string | null;

  customerRefundStatsByYear: ICustomerRefundStatisticsResponse | null;
  customerRefundStatsByYearLoading: boolean;
  customerRefundStatsByYearError: string | null;

  customerRefundHistory: ICustomerRefundHistoryResponse | null;
  customerRefundHistoryLoading: boolean;
  customerRefundHistoryError: string | null;

  customerReportHistory: ICustomerReportHistoryResponse[] | null;
  customerReportHistoryLoading: boolean;
  customerReportHistoryError: string | null;

  customerGeneratedReports: ICustomerGeneratedReport[] | null;
  customerGeneratedReportsLoading: boolean;
  customerGeneratedReportsError: string | null;

  generateReportLoading: boolean;
  generateReportError: string | null;

  customerMonthlyInvoices: IMonthlyInvoice[] | null;
  customerMonthlyInvoicesLoading: boolean;
  customerMonthlyInvoicesError: string | null;

  isMonthlyCustomer: boolean | null;
  isMonthlyCustomerLoading: boolean;

  customerReportData: ICustomerReportData | null;
  customerReportDataLoading: boolean;
  customerReportDataError: string | null;
}

const initialState: CustomerState = {
  customer: null,
  customerLoading: false,
  customerError: null,

  createCustomerResponse: null,
  createCustomerLoading: false,
  createCustomerError: null,

  updateCustomerResponse: null,
  updateCustomerLoading: false,
  updateCustomerError: null,

  customerCartInfo: null,
  customerCartInfoLoading: false,
  customerCartInfoError: null,

  bills: null,
  billsLoading: false,
  billsError: null,

  addStoreCreditResponse: null,
  addStoreCreditLoading: false,
  addStoreCreditError: null,

  deleteStoreCreditResponse: null,
  deleteStoreCreditLoading: false,
  deleteStoreCreditError: null,

  storeCreditHistoryResponse: null,
  getStoreCreditHistoryLoading: false,
  getStoreCreditHistoryError: null,

  addUserNoteResponse: null,
  addUserNoteLoading: false,
  addUserNoteError: null,

  updateUserNoteResponse: null,
  updateUserNoteLoading: false,
  updateUserNoteError: null,

  customerNotes: [],
  customerNotesLoading: false,
  customerNotesError: null,

  deleteCustomerNoteResponse: null,
  deleteCustomerNoteLoading: false,
  deleteCustomerNoteError: null,

  selectedBill: null,

  customerInsights: null,
  customerInsightsLoading: false,
  customerInsightsError: null,

  customerLatestBillDetails: null,
  customerLatestBillDetailsLoading: false,
  customerLatestBillDetailsError: null,

  customerCurrentYearServiceUsage: null,
  customerCurrentYearServiceUsageLoading: false,
  customerCurrentYearServiceUsageError: null,

  customerAllTimeServiceUsage: null,
  customerAllTimeServiceUsageLoading: false,
  customerAllTimeServiceUsageError: null,
  customerAvgSpendings: null,
  customerAvgSpendingsLoading: false,
  customerAvgSpendingsError: null,

  customerActivitiesByDay: null,
  customerActivitiesByDayLoading: false,
  customerActivitiesByDayError: null,

  customerOutstanding: null,
  customerOutstandingLoading: false,
  customerOutstandingError: null,

  invoiceHistory: null,
  invoiceHistoryLoading: false,
  invoiceHistoryError: null,

  assignNfcTagResponse: null,
  assignNfcTagLoading: false,
  assignNfcTagError: null,

  addNfcTagResponse: null,
  addNfcTagLoading: false,
  addNfcTagError: null,

  removeNfcTagLoading: false,
  removeNfcTagError: null,

  customerRefundStats: null,
  customerRefundStatsLoading: false,
  customerRefundStatsError: null,

  customerRefundStatsByYear: null,
  customerRefundStatsByYearLoading: false,
  customerRefundStatsByYearError: null,

  customerRefundHistory: null,
  customerRefundHistoryLoading: false,
  customerRefundHistoryError: null,

  customerReportHistory: null,
  customerReportHistoryLoading: false,
  customerReportHistoryError: null,

  customerGeneratedReports: null,
  customerGeneratedReportsLoading: false,
  customerGeneratedReportsError: null,

  generateReportLoading: false,
  generateReportError: null,

  customerMonthlyInvoices: null,
  customerMonthlyInvoicesLoading: false,
  customerMonthlyInvoicesError: null,

  isMonthlyCustomer: null,
  isMonthlyCustomerLoading: false,

  customerReportData: null,
  customerReportDataLoading: false,
  customerReportDataError: null,
};

const customerSlice = createSlice({
  name: "customer",
  initialState,
  reducers: {
    clearCustomer: (state: CustomerState) => {
      state.customerCartInfo = null;
      state.customer = null;
      state.customerError = null;
      state.customerLoading = false;
    },
    clearCustomerCartInfo: (state: CustomerState) => {
      state.customerCartInfo = null;
      state.customerCartInfoError = null;
      state.customerCartInfoLoading = false;
    },
    setSelectedBill: (state, action) => {
      state.selectedBill = action.payload;
    },
    clearCustomerNotes: (state: CustomerState) => {
      state.customerNotes = null;
      state.customerNotesError = null;
      state.customerNotesLoading = false;
    },
    clearCustomerInsights: (state: CustomerState) => {
      state.customerInsights = null;
      state.customerInsightsError = null;
      state.customerLatestBillDetails = null;
      state.customerLatestBillDetailsError = null;
      state.customerAvgSpendings = null;
      state.customerAvgSpendingsError = null;
      state.customerActivitiesByDay = null;
      state.customerActivitiesByDayError = null;
      state.customerActivitiesByDayLoading = false;
      state.customerOutstanding = null;
      state.customerOutstandingError = null;
      state.customerCurrentYearServiceUsage = null;
      state.customerAllTimeServiceUsage = null;
      state.customerRefundStats = null;
      state.customerRefundStatsError = null;
      state.customerRefundStatsByYear = null;
      state.customerRefundStatsByYearError = null;
      state.customerRefundHistory = null;
      state.customerRefundHistoryError = null;
      state.customerReportHistory = null;
      state.customerReportHistoryError = null;
      state.customerGeneratedReports = null;
      state.customerGeneratedReportsError = null;
      state.customerMonthlyInvoices = null;
      state.customerMonthlyInvoicesError = null;
      state.isMonthlyCustomer = null;
    },
    clearCustomerReportData: (state: CustomerState) => {
      state.customerReportData = null;
      state.customerReportDataLoading = false;
      state.customerReportDataError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCustomerByIdAction.rejected, (state: CustomerState) => {
      state.customer = null;
      state.customerError = "Customer fetching error";
      state.customerLoading = false;
    });
    builder.addCase(getCustomerByIdAction.pending, (state: CustomerState) => {
      state.customer = null;
      state.customerError = null;
      state.customerLoading = true;
    });
    builder.addCase(
      getCustomerByIdAction.fulfilled,
      (state: CustomerState, action) => {
        state.customer = action.payload;
        state.customerError = null;
        state.customerLoading = false;
      }
    );

    builder.addCase(
      getCustomerCartInfoByIdAction.rejected,
      (state: CustomerState) => {
        state.customerCartInfo = null;
        state.customerCartInfoError = "Customer's cart info fetching error";
        state.customerCartInfoLoading = false;
      }
    );
    builder.addCase(
      getCustomerCartInfoByIdAction.pending,
      (state: CustomerState) => {
        state.customerCartInfo = null;
        state.customerCartInfoError = null;
        state.customerCartInfoLoading = true;
      }
    );
    builder.addCase(
      getCustomerCartInfoByIdAction.fulfilled,
      (state: CustomerState, action) => {
        state.customerCartInfo = action.payload;
        state.customerCartInfoError = null;
        state.customerCartInfoLoading = false;
      }
    );

    builder.addCase(
      getCustomerBillHistoryAction.rejected,
      (state: CustomerState) => {
        state.bills = null;
        state.billsError = "Customer's bills fetching error";
        state.billsLoading = false;
      }
    );
    builder.addCase(
      getCustomerBillHistoryAction.pending,
      (state: CustomerState) => {
        state.bills = null;
        state.billsError = null;
        state.billsLoading = true;
      }
    );
    builder.addCase(
      getCustomerBillHistoryAction.fulfilled,
      (state: CustomerState, action) => {
        state.bills = action.payload;
        state.billsError = null;
        state.billsLoading = false;
      }
    );

    builder.addCase(addStoreCreditAction.rejected, (state: CustomerState) => {
      state.addStoreCreditResponse = null;
      state.addStoreCreditError = "Store credit adding error";
      state.addStoreCreditLoading = false;
    });
    builder.addCase(addStoreCreditAction.pending, (state: CustomerState) => {
      state.addStoreCreditResponse = null;
      state.addStoreCreditError = null;
      state.addStoreCreditLoading = true;
    });
    builder.addCase(
      addStoreCreditAction.fulfilled,
      (state: CustomerState, action) => {
        state.addStoreCreditResponse = action.payload;
        state.addStoreCreditError = null;
        state.addStoreCreditLoading = false;
      }
    );

    builder.addCase(
      deleteStoreCreditAction.rejected,
      (state: CustomerState) => {
        state.deleteStoreCreditResponse = null;
        state.deleteStoreCreditError = "Store credit deleting error";
        state.deleteStoreCreditLoading = false;
      }
    );
    builder.addCase(deleteStoreCreditAction.pending, (state: CustomerState) => {
      state.deleteStoreCreditResponse = null;
      state.deleteStoreCreditError = null;
      state.deleteStoreCreditLoading = true;
    });
    builder.addCase(
      deleteStoreCreditAction.fulfilled,
      (state: CustomerState, action) => {
        state.deleteStoreCreditResponse = action.payload as any;
        state.deleteStoreCreditError = null;
        state.deleteStoreCreditLoading = false;
      }
    );

    builder.addCase(
      getStoreCreditHistoryAction.rejected,
      (state: CustomerState) => {
        state.storeCreditHistoryResponse = null;
        state.getStoreCreditHistoryError =
          "Store credit history fetching error";
        state.getStoreCreditHistoryLoading = false;
      }
    );
    builder.addCase(
      getStoreCreditHistoryAction.pending,
      (state: CustomerState) => {
        state.storeCreditHistoryResponse = null;
        state.getStoreCreditHistoryError = null;
        state.getStoreCreditHistoryLoading = true;
      }
    );
    builder.addCase(
      getStoreCreditHistoryAction.fulfilled,
      (state: CustomerState, action) => {
        state.storeCreditHistoryResponse = action.payload;
        state.getStoreCreditHistoryError = null;
        state.getStoreCreditHistoryLoading = false;
      }
    );

    builder.addCase(addUserNoteAction.rejected, (state: CustomerState) => {
      state.addUserNoteResponse = null;
      state.addUserNoteError = "User note adding error";
      state.addUserNoteLoading = false;
    });
    builder.addCase(addUserNoteAction.pending, (state: CustomerState) => {
      state.addUserNoteResponse = null;
      state.addUserNoteError = null;
      state.addUserNoteLoading = true;
    });
    builder.addCase(
      addUserNoteAction.fulfilled,
      (state: CustomerState, action) => {
        state.addUserNoteResponse = action.payload;
        state.addUserNoteError = null;
        state.addUserNoteLoading = false;
      }
    );

    builder.addCase(updateUserNoteAction.rejected, (state: CustomerState) => {
      state.updateUserNoteResponse = null;
      state.updateUserNoteError = "Update user note failed";
      state.updateUserNoteLoading = false;
    });
    builder.addCase(updateUserNoteAction.pending, (state: CustomerState) => {
      state.updateUserNoteResponse = null;
      state.updateUserNoteError = null;
      state.updateUserNoteLoading = true;
    });
    builder.addCase(
      updateUserNoteAction.fulfilled,
      (state: CustomerState, action) => {
        state.updateUserNoteResponse = action.payload;
        state.updateUserNoteError = null;
        state.updateUserNoteLoading = false;
      }
    );

    builder.addCase(getUserNotesByIdAction.rejected, (state: CustomerState) => {
      state.customerNotes = null;
      state.customerNotesError = "User notes fetching error";
      state.customerNotesLoading = false;
    });
    builder.addCase(getUserNotesByIdAction.pending, (state: CustomerState) => {
      state.customerNotes = null;
      state.customerNotesError = null;
      state.customerNotesLoading = true;
    });
    builder.addCase(
      getUserNotesByIdAction.fulfilled,
      (state: CustomerState, action) => {
        state.customerNotes = action.payload;
        state.customerNotesError = null;
        state.customerNotesLoading = false;
      }
    );

    builder.addCase(deleteUserNoteAction.rejected, (state: CustomerState) => {
      state.deleteCustomerNoteResponse = null;
      state.deleteCustomerNoteError = "Delete user note failed";
      state.deleteCustomerNoteLoading = false;
    });
    builder.addCase(deleteUserNoteAction.pending, (state: CustomerState) => {
      state.deleteCustomerNoteResponse = null;
      state.deleteCustomerNoteError = null;
      state.deleteCustomerNoteLoading = true;
    });
    builder.addCase(
      deleteUserNoteAction.fulfilled,
      (state: CustomerState, action) => {
        state.deleteCustomerNoteResponse = action.payload;
        state.deleteCustomerNoteError = null;
        state.deleteCustomerNoteLoading = false;
      }
    );

    builder
      .addCase(getAllCustomerInsightsAction.pending, (state) => {
        state.customerInsights = null;
        state.customerInsightsLoading = true;
        state.customerInsightsError = null;
      })
      .addCase(getAllCustomerInsightsAction.fulfilled, (state, action) => {
        state.customerInsightsError = null;
        state.customerInsightsLoading = false;
        state.customerInsights = (action.payload as any).body;
      })
      .addCase(getAllCustomerInsightsAction.rejected, (state, action) => {
        state.customerInsights = null;
        state.customerInsightsLoading = false;
        state.customerInsightsError =
          (action.payload as any)?.message || "something went wrong";
      });

    builder
      .addCase(getAllCustomerLatestBillDetailsAction.pending, (state) => {
        state.customerLatestBillDetails = null;
        state.customerLatestBillDetailsLoading = true;
        state.customerLatestBillDetailsError = null;
      })
      .addCase(
        getAllCustomerLatestBillDetailsAction.fulfilled,
        (state, action) => {
          state.customerLatestBillDetailsError = null;
          state.customerLatestBillDetailsLoading = false;
          state.customerLatestBillDetails = (action.payload as any).body;
        }
      )
      .addCase(
        getAllCustomerLatestBillDetailsAction.rejected,
        (state, action) => {
          state.customerLatestBillDetails = null;
          state.customerLatestBillDetailsLoading = false;
          state.customerLatestBillDetailsError =
            (action.payload as any)?.message || "something went wrong";
        }
      );

    builder
      .addCase(getAllCustomerCurrentYearServiceUsageAction.pending, (state) => {
        state.customerCurrentYearServiceUsage = null;
        state.customerCurrentYearServiceUsageLoading = true;
        state.customerCurrentYearServiceUsageError = null;
      })
      .addCase(
        getAllCustomerCurrentYearServiceUsageAction.fulfilled,
        (state, action) => {
          state.customerCurrentYearServiceUsageError = null;
          state.customerCurrentYearServiceUsageLoading = false;
          state.customerCurrentYearServiceUsage = (action.payload as any).body;
        }
      )
      .addCase(
        getAllCustomerCurrentYearServiceUsageAction.rejected,
        (state, action) => {
          state.customerCurrentYearServiceUsage = null;
          state.customerCurrentYearServiceUsageLoading = false;
          state.customerCurrentYearServiceUsageError =
            (action.payload as any)?.message || "something went wrong";
        }
      );

    builder
      .addCase(getAllCustomerAllTimeServiceUsageAction.pending, (state) => {
        state.customerAllTimeServiceUsage = null;
        state.customerAllTimeServiceUsageLoading = true;
        state.customerAllTimeServiceUsageError = null;
      })
      .addCase(
        getAllCustomerAllTimeServiceUsageAction.fulfilled,
        (state, action) => {
          state.customerAllTimeServiceUsageError = null;
          state.customerAllTimeServiceUsageLoading = false;
          state.customerAllTimeServiceUsage = (action.payload as any).body;
        }
      )
      .addCase(
        getAllCustomerAllTimeServiceUsageAction.rejected,
        (state, action) => {
          state.customerAllTimeServiceUsage = null;
          state.customerAllTimeServiceUsageLoading = false;
          state.customerAllTimeServiceUsageError =
            (action.payload as any)?.message || "something went wrong";
        }
      );

    builder
      .addCase(getAllCustomerAvgSpendingsAction.pending, (state) => {
        state.customerAvgSpendings = null;
        state.customerAvgSpendingsLoading = true;
        state.customerAvgSpendingsError = null;
      })
      .addCase(getAllCustomerAvgSpendingsAction.fulfilled, (state, action) => {
        state.customerAvgSpendingsError = null;
        state.customerAvgSpendingsLoading = false;
        state.customerAvgSpendings = (action.payload as any).body;
      })
      .addCase(getAllCustomerAvgSpendingsAction.rejected, (state, action) => {
        state.customerAvgSpendings = null;
        state.customerAvgSpendingsLoading = false;
        state.customerAvgSpendingsError =
          (action.payload as any)?.message || "something went wrong";
      });
    builder.addCase(getCustomerActivitiesByDayAction.pending, (state) => {
      state.customerActivitiesByDay = null;
      state.customerActivitiesByDayLoading = true;
      state.customerActivitiesByDayError = null;
    });
    builder.addCase(
      getCustomerActivitiesByDayAction.fulfilled,
      (state, action) => {
        state.customerActivitiesByDay = (action.payload as any).body;
        state.customerActivitiesByDayLoading = false;
        state.customerActivitiesByDayError = null;
      }
    );
    builder.addCase(
      getCustomerActivitiesByDayAction.rejected,
      (state, action) => {
        state.customerActivitiesByDay = null;
        state.customerActivitiesByDayLoading = false;
        state.customerActivitiesByDayError =
          (action.payload as any)?.message || "something went wrong";
      }
    );

    builder.addCase(getCustomerOutstandingAction.pending, (state) => {
      state.customerOutstanding = null;
      state.customerOutstandingLoading = true;
      state.customerOutstandingError = null;
    });
    builder.addCase(getCustomerOutstandingAction.fulfilled, (state, action) => {
      state.customerOutstanding = (action.payload as any).body;
      state.customerOutstandingLoading = false;
      state.customerOutstandingError = null;
    });
    builder.addCase(getCustomerOutstandingAction.rejected, (state, action) => {
      state.customerOutstanding = null;
      state.customerOutstandingLoading = false;
      state.customerOutstandingError =
        (action.payload as any)?.message || "something went wrong";
    });

    builder.addCase(getInvoiceHistoryAction.pending, (state) => {
      state.invoiceHistory = null;
      state.invoiceHistoryLoading = true;
      state.invoiceHistoryError = null;
    });

    builder.addCase(getInvoiceHistoryAction.fulfilled, (state, action) => {
      state.invoiceHistory = (
        action.payload as InvoiceHistoryResponse[][]
      ).flat();
      state.invoiceHistoryLoading = false;
      state.invoiceHistoryError = null;
    });

    builder.addCase(getInvoiceHistoryAction.rejected, (state) => {
      state.invoiceHistory = null;
      state.invoiceHistoryLoading = false;
      state.invoiceHistoryError = "Invoice history fetching error";
    });

    builder.addCase(assignCustomerNfcTagAction.pending, (state) => {
      state.assignNfcTagLoading = true;
      state.assignNfcTagError = null;
    });
    builder.addCase(assignCustomerNfcTagAction.fulfilled, (state, action) => {
      state.assignNfcTagResponse = action.payload ?? null;
      state.assignNfcTagLoading = false;
      state.assignNfcTagError = null;
      if (state.customer && action.payload) {
        state.customer.nfcTagId = action.payload.nfcTagId;
        state.customer.nfcTagAssignedOn = action.payload.nfcTagAssignedOn;
        state.customer.nfcTagAssignedBy = action.payload.nfcTagAssignedBy;
      }
    });
    builder.addCase(assignCustomerNfcTagAction.rejected, (state) => {
      state.assignNfcTagLoading = false;
      state.assignNfcTagError = "NFC tag assignment failed";
    });

    builder.addCase(addCustomerNfcTagAction.pending, (state) => {
      state.addNfcTagLoading = true;
      state.addNfcTagError = null;
    });
    builder.addCase(addCustomerNfcTagAction.fulfilled, (state, action) => {
      state.addNfcTagResponse = action.payload ?? null;
      state.addNfcTagLoading = false;
      state.addNfcTagError = null;
      if (state.customer && action.payload) {
        state.customer.nfcTags = [...(state.customer.nfcTags ?? []), action.payload];
      }
    });
    builder.addCase(addCustomerNfcTagAction.rejected, (state) => {
      state.addNfcTagLoading = false;
      state.addNfcTagError = "NFC tag add failed";
    });

    builder.addCase(removeCustomerNfcTagAction.pending, (state) => {
      state.removeNfcTagLoading = true;
      state.removeNfcTagError = null;
    });
    builder.addCase(removeCustomerNfcTagAction.fulfilled, (state, action) => {
      state.removeNfcTagLoading = false;
      state.removeNfcTagError = null;
      if (state.customer) {
        state.customer.nfcTags = (state.customer.nfcTags ?? []).filter(
          (tag) => tag.nfcTagId !== action.payload
        );
      }
    });
    builder.addCase(removeCustomerNfcTagAction.rejected, (state) => {
      state.removeNfcTagLoading = false;
      state.removeNfcTagError = "NFC tag removal failed";
    });

    builder.addCase(getCustomerRefundStatsAction.pending, (state) => {
      state.customerRefundStats = null;
      state.customerRefundStatsLoading = true;
      state.customerRefundStatsError = null;
    });
    builder.addCase(getCustomerRefundStatsAction.fulfilled, (state, action) => {
      state.customerRefundStats = (action.payload as any).body;
      state.customerRefundStatsLoading = false;
      state.customerRefundStatsError = null;
    });
    builder.addCase(getCustomerRefundStatsAction.rejected, (state, action) => {
      state.customerRefundStats = null;
      state.customerRefundStatsLoading = false;
      state.customerRefundStatsError =
        (action.payload as any)?.message || "something went wrong";
    });

    builder.addCase(getCustomerRefundStatsByYearAction.pending, (state) => {
      state.customerRefundStatsByYear = null;
      state.customerRefundStatsByYearLoading = true;
      state.customerRefundStatsByYearError = null;
    });
    builder.addCase(
      getCustomerRefundStatsByYearAction.fulfilled,
      (state, action) => {
        state.customerRefundStatsByYear = (action.payload as any).body;
        state.customerRefundStatsByYearLoading = false;
        state.customerRefundStatsByYearError = null;
      }
    );
    builder.addCase(
      getCustomerRefundStatsByYearAction.rejected,
      (state, action) => {
        state.customerRefundStatsByYear = null;
        state.customerRefundStatsByYearLoading = false;
        state.customerRefundStatsByYearError =
          (action.payload as any)?.message || "something went wrong";
      }
    );

    builder.addCase(getCustomerRefundHistoryAction.pending, (state) => {
      state.customerRefundHistory = null;
      state.customerRefundHistoryLoading = true;
      state.customerRefundHistoryError = null;
    });
    builder.addCase(
      getCustomerRefundHistoryAction.fulfilled,
      (state, action) => {
        state.customerRefundHistory = (action.payload as any).body;
        state.customerRefundHistoryLoading = false;
        state.customerRefundHistoryError = null;
      }
    );
    builder.addCase(
      getCustomerRefundHistoryAction.rejected,
      (state, action) => {
        state.customerRefundHistory = null;
        state.customerRefundHistoryLoading = false;
        state.customerRefundHistoryError =
          (action.payload as any)?.message || "something went wrong";
      }
    );

    builder.addCase(getCustomerReportHistoryAction.pending, (state) => {
      state.customerReportHistory = null;
      state.customerReportHistoryLoading = true;
      state.customerReportHistoryError = null;
    });
    builder.addCase(getCustomerReportHistoryAction.fulfilled, (state, action) => {
      state.customerReportHistory = action.payload ?? null;
      state.customerReportHistoryLoading = false;
      state.customerReportHistoryError = null;
    });
    builder.addCase(getCustomerReportHistoryAction.rejected, (state, action) => {
      state.customerReportHistory = null;
      state.customerReportHistoryLoading = false;
      state.customerReportHistoryError =
        (action.payload as any)?.message || "something went wrong";
    });

    // Generated Reports
    builder.addCase(getCustomerGeneratedReportsAction.pending, (state) => {
      state.customerGeneratedReportsLoading = true;
      state.customerGeneratedReportsError = null;
    });
    builder.addCase(getCustomerGeneratedReportsAction.fulfilled, (state, action) => {
      state.customerGeneratedReports = action.payload ?? null;
      state.customerGeneratedReportsLoading = false;
      state.customerGeneratedReportsError = null;
    });
    builder.addCase(getCustomerGeneratedReportsAction.rejected, (state, action) => {
      state.customerGeneratedReportsLoading = false;
      state.customerGeneratedReportsError =
        (action.payload as any)?.message || "something went wrong";
    });

    // Generate Report (POST)
    builder.addCase(generateCustomerReportAction.pending, (state) => {
      state.generateReportLoading = true;
      state.generateReportError = null;
    });
    builder.addCase(generateCustomerReportAction.fulfilled, (state, action) => {
      state.generateReportLoading = false;
      state.generateReportError = null;
      if (action.payload) {
        state.customerGeneratedReports = [
          action.payload,
          ...(state.customerGeneratedReports ?? []),
        ];
      }
    });
    builder.addCase(generateCustomerReportAction.rejected, (state, action) => {
      state.generateReportLoading = false;
      state.generateReportError =
        (action.payload as any)?.message || "something went wrong";
    });

    // Monthly Invoices
    builder.addCase(getCustomerMonthlyInvoicesAction.pending, (state) => {
      state.customerMonthlyInvoicesLoading = true;
      state.customerMonthlyInvoicesError = null;
    });
    builder.addCase(getCustomerMonthlyInvoicesAction.fulfilled, (state, action) => {
      state.customerMonthlyInvoices = action.payload ?? null;
      state.customerMonthlyInvoicesLoading = false;
      state.customerMonthlyInvoicesError = null;
    });
    builder.addCase(getCustomerMonthlyInvoicesAction.rejected, (state, action) => {
      state.customerMonthlyInvoicesLoading = false;
      state.customerMonthlyInvoicesError =
        (action.payload as any)?.message || "something went wrong";
    });

    // Is Monthly Customer
    builder.addCase(getIsMonthlyCustomerAction.pending, (state) => {
      state.isMonthlyCustomerLoading = true;
    });
    builder.addCase(getIsMonthlyCustomerAction.fulfilled, (state, action) => {
      state.isMonthlyCustomer = action.payload ?? null;
      state.isMonthlyCustomerLoading = false;
    });
    builder.addCase(getIsMonthlyCustomerAction.rejected, (state) => {
      state.isMonthlyCustomer = false;
      state.isMonthlyCustomerLoading = false;
    });
    builder.addCase(getCustomerReportDataAction.pending, (state) => {
      state.customerReportDataLoading = true;
      state.customerReportDataError = null;
    });
    builder.addCase(getCustomerReportDataAction.fulfilled, (state, action) => {
      state.customerReportData = action.payload ?? null;
      state.customerReportDataLoading = false;
    });
    builder.addCase(getCustomerReportDataAction.rejected, (state, action) => {
      state.customerReportDataError = (action.payload as any)?.message ?? "Failed to load report data";
      state.customerReportDataLoading = false;
    });
  },
});

export default customerSlice.reducer;
export const CustomerSliceAction = customerSlice.actions;
